import {Injectable} from '@angular/core';
import {EventService} from './event.service';
import {CognitoAccessToken, CognitoIdToken, CognitoRefreshToken} from 'amazon-cognito-identity-js';
import {UserEvent} from '../models/global.model';
import {_accessTokenKey, _emailKey, _idTokenKey, _refreshTokenKey, _userAccessLevelKey, _userKey} from '../localstorage-keys';
import {emptyUser, IUserData, UserAccessLevel} from '../models/user.model';
import {safeParse} from '../helpers/global.helper';
import {environment} from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class UserService extends EventService {
  private user: IUserData | null = null;
  private idToken: CognitoIdToken | null = null;
  private accessToken: CognitoAccessToken | null = null;
  private refreshToken: CognitoRefreshToken | null = null;
  private newPasswordRequiredUser: string | null = null;
  private userAccessLevel: UserAccessLevel;

  private authData: {email: string; password: string} = {email: '', password: ''};

  constructor() {
    super();
    // Needed for auto-login if the user has not logged out
    this.getUserData();
    this.fetchTokens();
    this.fetchUserAccessLevel();
  }

  public getUser(): IUserData | null {
    return this.user;
  }

  public getUserStatus(): boolean {
    // Todo wish of the user is active or not
    // this.user?.tenantLock
    return this.user?.isActive as boolean;
  }

  public setUserData(user: IUserData) {
    // Always use the userAccessLevel from Cognito token (the source of truth)
    if (this.userAccessLevel) {
      user.userAccessLevel = this.userAccessLevel as any;
    }
    this.user = user;
  }

  public getIdToken(): CognitoIdToken | null {
    if (!this.idToken) this.fetchTokens();
    return this.idToken;
  }

  public getAccessToken(): CognitoAccessToken | null {
    //if (!this.accessToken)
    try {
      this.fetchTokens();
      return this.accessToken;
    } catch (e) {
      return null;
    }
  }

  public getRefreshToken(): CognitoRefreshToken | null {
    if (!this.refreshToken) this.fetchTokens();
    return this.refreshToken;
  }

  public logout() {
    this.storeTokens(null, null, null);
    localStorage.removeItem(_userAccessLevelKey);
    localStorage.removeItem(_emailKey);
    this.clearTenantScopedCache();
    this.user = null;
    this.emit(UserEvent.loggedOut);
  }

  private clearTenantScopedCache() {
    const prefix = environment.appId + '.customersCount';
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(prefix)) sessionStorage.removeItem(key);
    }
  }

  public login(idToken: CognitoIdToken, accessToken: CognitoAccessToken, refreshToken: CognitoRefreshToken) {
    this.setUserAccessLevel(idToken.payload['custom:userAccessLevel'] as UserAccessLevel);

    this.storeTokens(idToken, accessToken, refreshToken);
    this.emit(UserEvent.loggedIn);
  }

  public isLoggedIn(): boolean {
    return !!this.idToken;
  }

  public getLocalUserData(): IUserData {
    return safeParse(localStorage.getItem(_userKey) || '', null);
  }

  public getUserData(): any {
    const localData = this.getLocalUserData();
    this.user = {...emptyUser, ...localData, ...this.user};
    return this.user;
  }

  public storeTokens(idToken: CognitoIdToken | null, accessToken: CognitoAccessToken | null, refreshToken: CognitoRefreshToken | null) {
    // remove the tokens that are null, otherwise store (or override) them
    this.idToken = idToken;
    if (idToken) {
      localStorage.setItem(_idTokenKey, this.idToken?.getJwtToken() || '');
    } else {
      localStorage.removeItem(_idTokenKey);
    }

    this.accessToken = accessToken;
    if (accessToken) {
      localStorage.setItem(_accessTokenKey, this.accessToken?.getJwtToken() || '');
    } else {
      localStorage.removeItem(_accessTokenKey);
    }

    this.refreshToken = refreshToken;
    if (refreshToken) {
      localStorage.setItem(_refreshTokenKey, this.refreshToken?.getToken() || '');
    } else {
      localStorage.removeItem(_refreshTokenKey);
    }
  }

  public fetchTokens() {
    const IdToken = localStorage.getItem(_idTokenKey);
    if (IdToken) this.idToken = new CognitoIdToken({IdToken});

    const AccessToken = localStorage.getItem(_accessTokenKey);
    if (AccessToken) this.accessToken = new CognitoAccessToken({AccessToken});

    const RefreshToken = localStorage.getItem(_refreshTokenKey);
    if (RefreshToken) this.refreshToken = new CognitoRefreshToken({RefreshToken});
  }

  public setPostSignInPage(page = '') {
    if (page) {
      sessionStorage.setItem(environment.appId + '.POST_SIGNIN_PAGE', page);
    } else {
      sessionStorage.removeItem(environment.appId + '.POST_SIGNIN_PAGE');
    }
    sessionStorage.setItem(environment.appId + '.POST_SIGNIN_PAGE', page);
  }

  public getPostSignInPage(): string {
    const postSignInPage = sessionStorage.getItem(environment.appId + '.POST_SIGNIN_PAGE');
    return typeof postSignInPage === 'string' ? postSignInPage : '';
  }

  public setUserAuthData(email?: string, password?: string) {
    if (email) localStorage.setItem(_emailKey, email);
    this.authData = {email: email || '', password: password || ''};
  }

  public getUserAuthData(): {email: string; password: string} {
    return this.authData;
  }

  public canLogin(): boolean {
    return !!this.authData.email && !!this.authData.password;
  }

  public setNewPasswordRequiredUser(email: string) {
    this.newPasswordRequiredUser = email;
  }

  public getNewPasswordRequiredUser(): string | null {
    return this.newPasswordRequiredUser;
  }

  public clearNewPasswordRequiredUser() {
    this.newPasswordRequiredUser = null;
  }

  public setUserAccessLevel(level: UserAccessLevel) {
    this.userAccessLevel = level;
    localStorage.setItem(_userAccessLevelKey, level);
  }

  public getUserAccessLevel(): UserAccessLevel {
    return this.userAccessLevel;
  }

  private fetchUserAccessLevel() {
    const storedLevel = localStorage.getItem(_userAccessLevelKey);
    if (storedLevel) {
      this.userAccessLevel = storedLevel as UserAccessLevel;
    }
  }
}
